/**
 * AddNodeMenu — "+" button that opens a popover with allowed node types.
 * Context-aware: uses ALLOWED_CHILDREN to only show valid options.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useBuilderStore } from '../../stores/builderStore';
import { NODE_LABELS, NODE_DESCRIPTIONS, NODE_COLORS } from '../../types/builder';
import type { NodeType } from '../../types/builder';

interface AddNodeMenuProps {
  parentId: string;
  allowedTypes: NodeType[];
  insertIndex?: number;
}

export function AddNodeMenu({ parentId, allowedTypes, insertIndex }: AddNodeMenuProps) {
  const addNode = useBuilderStore((s) => s.addNode);
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleAdd = useCallback(
    (type: NodeType) => {
      addNode(parentId, type, insertIndex);
      setOpen(false);
    },
    [addNode, parentId, insertIndex],
  );

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  if (allowedTypes.length === 0) return null;

  return (
    <div ref={ref} className="relative inline-flex">
      <button
        onClick={() => setOpen(!open)}
        className={clsx(
          'w-6 h-6 flex items-center justify-center rounded-md transition-all duration-150',
          'text-surface-500 hover:text-primary-400 hover:bg-primary-500/10',
          'border border-dashed border-surface-600 hover:border-primary-500/40',
        )}
        title="Add node"
      >
        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      </button>

      {open && (
        <div
          className={clsx(
            'absolute z-50 left-8 top-0 min-w-[220px]',
            'bg-surface-800 border border-surface-600 rounded-xl shadow-2xl',
            'py-1.5 animate-in fade-in slide-in-from-left-2 duration-150',
          )}
        >
          <div className="px-3 py-1.5 text-[10px] font-semibold text-surface-500 uppercase tracking-wider">
            Add Node
          </div>
          {allowedTypes.map((type) => {
            const colors = NODE_COLORS[type];
            return (
              <button
                key={type}
                onClick={() => handleAdd(type)}
                className={clsx(
                  'w-full flex items-center gap-3 px-3 py-2 text-left',
                  'hover:bg-surface-700/50 transition-colors duration-100',
                )}
              >
                <span className={clsx('w-2 h-2 rounded-full shrink-0', colors.text, 'bg-current')} />
                <div className="min-w-0">
                  <div className="text-sm font-medium text-surface-200">{NODE_LABELS[type]}</div>
                  <div className="text-[11px] text-surface-500 truncate">{NODE_DESCRIPTIONS[type]}</div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
